import { IDirective, IScope, IAugmentedJQuery, ICompileService, IDirectiveFactory, IFilterService } from "angular";
import { parse } from "marked";
import { AssigneeActions, IAssignee, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { IdMap } from "@gtmhub/util";
import { inlineMarkedOptions, markedOptions } from "./util";

const regex = /@\[(.*?)\]/g;

const bindStateToThis = (state: IAssigneesStoreState): Record<string, unknown> => {
  return {
    assigneesIdMap: state.assignees.map,
  };
};

export interface IMentioningScope extends IScope {
  text: string;
  inline: boolean;
  placeholder: string;
  searchTerm: string;
  suffix?: {
    text: string;
    className?: string;
    hidden?: boolean;
  };
}

export class Mentioning implements IDirective {
  public scope = {
    text: "@",
    inline: "@",
    placeholder: "@",
    searchTerm: "@",
    suffix: "<",
  };
  public restrict = "E";
  public transclude = true;

  constructor(private $compile: ICompileService, private $filter: IFilterService, private $ngRedux: INgRedux, private assigneeActions: AssigneeActions) {}

  public link(scope: IMentioningScope, element: IAugmentedJQuery): void {
    this.$ngRedux.connect(bindStateToThis)(scope);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    const renderText = (assigneeIdMap: IdMap<IAssignee>): void => {
      const options = scope.inline ? inlineMarkedOptions : markedOptions;
      let text = this.parseMention(parse(scope.text, options), assigneeIdMap, scope.inline);

      if (scope.searchTerm) {
        text = this.$filter<(text: string, search: string) => string>("bolder")(text, scope.searchTerm);
      }

      const contentEl = document.createElement("div");
      contentEl.classList.add("mentioned-wrapper");
      contentEl.innerHTML = text;

      if (scope.suffix && !scope.suffix.hidden) {
        const suffixElement = document.createElement("span");
        suffixElement.classList.add(scope.suffix.className);
        suffixElement.innerHTML = scope.suffix.text;

        const paragraphs = contentEl.querySelectorAll("p");
        const lastParagraph = paragraphs.item(paragraphs.length - 1);

        const container = lastParagraph || contentEl;
        container.innerHTML += suffixElement.outerHTML;
      }

      if (scope.placeholder && scope.text === "-") {
        contentEl.classList.add("mentioned-placeholder");
        contentEl.innerHTML = scope.placeholder;
      }

      this.compileMentions(contentEl, scope);

      element[0].innerHTML = "";
      element[0].appendChild(contentEl);
    };

    scope.$watchGroup(["assigneesIdMap", "text"], ([assigneesMap, text]) => {
      if (Object.values(assigneesMap).length && text) {
        renderText(assigneesMap as IdMap<IAssignee>);
      }
    });
  }

  private parseMention = (text: string, assigneesIdMap: IdMap<IAssignee>, inline: boolean) => {
    const items = text.match(regex) || [];
    for (let i = 0; i < items.length; i++) {
      const splitedText = items[i].split(":");
      const twoNames = splitedText[0].split("@[")[1];
      const id = splitedText[splitedText.length - 1].split("]")[0];
      const assignee = assigneesIdMap[id] ? assigneesIdMap[id] : { name: twoNames };

      if (inline) {
        text = text.replace(items[i], `@${assignee.name}`);
      } else {
        const mentionedEl = document.createElement("mentioned");
        mentionedEl.setAttribute("assignee", JSON.stringify(assignee));
        mentionedEl.innerHTML = assignee.name;
        const name = mentionedEl.outerHTML;
        text = text.replace(items[i], name);
      }
    }

    return text;
  };

  private compileMentions = (contentEl: HTMLElement, scope: IScope) => {
    const mentions = contentEl.querySelectorAll("mentioned");
    for (let i = 0; i < mentions.length; i++) {
      const mentionEl = mentions.item(i);
      const mentionHtml = mentionEl.outerHTML;
      mentionEl.replaceWith(this.$compile(mentionHtml)(scope)[0]);
    }
  };

  public static factory(): IDirectiveFactory {
    const directive = ($compile: ICompileService, $filter: IFilterService, $ngRedux: INgRedux, assigneeActions: AssigneeActions) =>
      new Mentioning($compile, $filter, $ngRedux, assigneeActions);
    directive.$inject = ["$compile", "$filter", "$ngRedux", "AssigneeActions"];
    return directive;
  }
}

export function MentioningFilterFactory() {
  return function MentioningFilter(text: string): string {
    if (!text) {
      return text;
    }

    const items = text.match(regex);
    if (!items) {
      return text;
    }

    for (let i = 0; i < items.length; i++) {
      const name = items[i].split(":")[0].split("@[")[1];
      text = text.replace(items[i], name);
    }

    return text;
  };
}
